import { useState, useEffect, useContext } from "react";
import { useForm, Controller } from "react-hook-form";
import { Col, Row } from "antd";
import { FaCheck, FaPaste } from "react-icons/fa";
import { FaX } from "react-icons/fa6";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "react-query";
import { toast } from "react-hot-toast";

import { EnumTipoConfigGroupSign } from "constants/EnumTipoConfigGroupSign";
import { UserContext } from "context/UserContext";
import BodyPanel from "shared/components/panel/BodyPanel";
import HeaderPanel from "shared/components/panel/HeaderPanel";
import { Panel } from "shared/components/panel/Panel";
import FooterPanel from "shared/components/panel/FooterPanel";
import { Section } from "shared/components/section/Section";
import SelectAsync from "shared/components/select/SelectAsync";
import { api } from "constants/RotasAPI";
import { ConfigAssDigitalDocumentoPaginadaDTO } from "types/dto/groupsign/ConfigAssDigitalDocumentoPaginadaDTO";
import { EnumTipoDocumentoAssDigital, getDocumentoFormatado } from "constants/EnumTipoDocumentoAssDigital";
import { EnumTipoPrazoDocumentoAssDigital, getPrazoFormatado } from "constants/EnumTipoPrazoDocumentoAssDigital";
import CustomSelect from "shared/components/select/CustomSelect";
import { SelectOptionType } from "types/select/SelectOptionType";
import { CustomLabel } from "shared/components/input/CustomLabel";
import { TipoLembreteAssinaturaDigital, getLembreteFormatado } from "constants/EnumTipoLembreteAssDigital";
import { green, grey } from "@mui/material/colors";
import { Radio, FormControlLabel } from "@mui/material";
import CustomButton from "shared/components/button/CustomButton";
import { usePostConfigDocumento } from "hooks/groupsign/usePostConfigDocumento";
import { Rotas } from "constants/EnumRotas";
import showAlert from "shared/util/functions/alert/showAlert";

interface ConfigDocumentoForm {
  condominio: SelectOptionType<number> | null;
  tipoDocumento: SelectOptionType<EnumTipoDocumentoAssDigital>;
  tipoPrazo: SelectOptionType<EnumTipoPrazoDocumentoAssDigital>;
  tipoLembrete: SelectOptionType<TipoLembreteAssinaturaDigital>;
  permitirRubrica: boolean;
  enviarEmOrdem: boolean;
  publicarParaCondominos: boolean;
}

export const GerenciarConfiguracaoDocumento: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { usuarioLogado } = useContext(UserContext);

  const configFromState: ConfigAssDigitalDocumentoPaginadaDTO | undefined = location.state?.config;
  const isEditMode = Boolean(configFromState);

  const { control, register, setValue, handleSubmit, setError, formState: { errors, isDirty } } = useForm<ConfigDocumentoForm>({
    defaultValues: {
      condominio: null,
      tipoDocumento: {
        value: EnumTipoDocumentoAssDigital.PASTA_PRESTACAO_CONTAS,
        label: getDocumentoFormatado(EnumTipoDocumentoAssDigital.PASTA_PRESTACAO_CONTAS),
      },
      tipoPrazo: {
        value: EnumTipoPrazoDocumentoAssDigital.UM_DIA,
        label: getPrazoFormatado(EnumTipoPrazoDocumentoAssDigital.UM_DIA),
      },
      tipoLembrete: {
        value: TipoLembreteAssinaturaDigital.A_CADA_1_DIA,
        label: getLembreteFormatado(TipoLembreteAssinaturaDigital.A_CADA_1_DIA),
      },
      permitirRubrica: false,
      enviarEmOrdem: false,
      publicarParaCondominos: false,
    }
  });

  useEffect(() => {
    if (isEditMode && configFromState) {
      setValue("condominio", {
        value: configFromState.condominioId,
        label: configFromState.nomeCondominio ?? "",
      });
      setValue("tipoDocumento", {
        value: configFromState.tipoDocumento,
        label: getDocumentoFormatado(configFromState.tipoDocumento),
      });
      setValue("tipoPrazo", {
        value: configFromState.tipoPrazo,
        label: getPrazoFormatado(configFromState.tipoPrazo),
      });
      setValue("tipoLembrete", {
        value: configFromState.tipoLembrete,
        label: getLembreteFormatado(configFromState.tipoLembrete),
      });
      setValue("permitirRubrica", configFromState.permitirRubrica);
      setValue("enviarEmOrdem", configFromState.enviarEmOrdem);
      setValue("publicarParaCondominos", configFromState.publicarParaCondominos);
    }
  }, [configFromState, isEditMode, setValue]);

  const { mutate: salvarConfiguracao, isLoading: isSaving } = usePostConfigDocumento();

  const handleCondominioChange = (selectedOption: any) => {
    setValue("condominio", selectedOption);
  };

  const handleCancelar = () => {
    isDirty 
      ? exibirAlertConfirmacao() 
      : closeModal()
  };

  const exibirAlertConfirmacao = () => {
      showAlert({
        texto: "Existem alterações que não foram salvas na tela. Tem certeza que deseja sair sem salvar?",
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        onConfirm: () => closeModal()
      });
    };

  const enumToSelectOption = <T,>(enumValue: T, formatFunction: (value: T) => string): SelectOptionType<T> => ({
    value: enumValue,
    label: formatFunction(enumValue),
  });

  const opcoesTipoDocumento = Object.values(EnumTipoDocumentoAssDigital).map((tipo) =>
    enumToSelectOption(tipo, getDocumentoFormatado)
  );
  const opcoesTipoPrazo = Object.values(EnumTipoPrazoDocumentoAssDigital).map((prazo) =>
    enumToSelectOption(prazo, getPrazoFormatado)
  );
  const opcoesTipoLembrete = Object.values(TipoLembreteAssinaturaDigital).map((lembrete) =>
    enumToSelectOption(lembrete, getLembreteFormatado)
  );

  const closeModal = () => {
    navigate(`${Rotas.ASSINATURA_DIGIAL_CONFIGURACAO}?tab=${EnumTipoConfigGroupSign.CONFIGURACAO_DOCUMENTOS}`);
  };

  const onSubmit = (data: ConfigDocumentoForm) => {
    if (!data.condominio) {
      setError("condominio", { type: "manual", message: "Condomínio é obrigatório!" });
      return;
    }

    const payload: ConfigAssDigitalDocumentoPaginadaDTO = {
      id: configFromState?.id,
      condominioId: data.condominio.value,
      nomeCondominio: data.condominio.label,
      tipoDocumento: data.tipoDocumento.value,
      tipoPrazo: data.tipoPrazo.value,
      tipoLembrete: data.tipoLembrete.value,
      permitirRubrica: data.permitirRubrica,
      enviarEmOrdem: data.enviarEmOrdem,
      publicarParaCondominos: data.publicarParaCondominos,
      isAtivo: true,
      dataHoraConfiguracao: new Date(),
    };

    salvarConfiguracao(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries("configuracoes-documentos");
        isEditMode ?        
        toast.success("Configuração de assinatura digital editada com sucesso."):        
        toast.success("Configuração de assinatura digital salva com sucesso.");
        closeModal();
      },
    });
  };

  return (
    <Panel
      headerPanel={
        <HeaderPanel
          title={isEditMode ? "Editar Configuração" : "Nova Configuração"}
          ocultarCondominio={!usuarioLogado.condominioContextoId}
          abaAtual={EnumTipoConfigGroupSign.CONFIGURACAO_DOCUMENTOS}
        />
      }
      footerPanel={
        <FooterPanel
          title="Ações"
          botoes={[
            <CustomButton
              key="btnCancelar"
              id="btnCancelar"
              variant="danger-alt"
              label="Cancelar"
              onClick={handleCancelar}
              leftIcon={FaX}
            />,
            <CustomButton
              key="btnSalvar"
              id="btnSalvar"
              variant="primary"
              label="Salvar"
              onClick={handleSubmit(onSubmit)}
              disabled={isSaving}
              leftIcon={FaCheck}
            />,
          ]}
        />
      }
    >

      <BodyPanel>
        <Section title={isEditMode ? "EDITAR DOCUMENTO" : "NOVO DOCUMENTO"} icon={FaPaste}>
          <div className="ms-4 mt-4">
            <Col xl={12} sm={12} xs={12}>
              <SelectAsync
                id="selecionar-condominio"
                label="Condomínio"
                endpoint={api.condominio.getRegistrosCondominios}
                placeholder="Selecione o condomínio"
                nomeAtributoValue="id"
                nomeAtributoLabel="nomeFantasia"
                onChange={handleCondominioChange}
                name="condominio"
                control={control}
                required
                usarValidacaoParaFormulario={true}
                register={register}
                setValue={setValue}
              />
            </Col>
            <Col xl={6} sm={6} xs={6} className="mt-4">
              <CustomSelect
                id="tipo-documento"
                label="Tipo de relatório/documento"
                options={opcoesTipoDocumento}
                name="tipoDocumento"
                control={control}
                usarValidacaoParaFormulario={true}
                register={register}
                setValue={setValue}
              />
            </Col>
            <Row gutter={[5, 16]} className="mt-4">
              <CustomLabel>Parâmetros</CustomLabel>
            </Row>
            <Row gutter={[5, 16]}>
              <Col xl={6} sm={6} xs={6} className="mt-4">
                <CustomSelect
                  id="tipo-prazo"
                  label="Prazo para assinatura"
                  options={opcoesTipoPrazo}
                  name="tipoPrazo"
                  control={control}
                  usarValidacaoParaFormulario={true}
                  register={register}
                  setValue={setValue}
                />
              </Col>
              <Col xl={6} sm={6} xs={6} className="mt-4">
                <CustomSelect
                  id="tipo-lembrete"
                  label="Lembrete de assinatura por e-mail"
                  options={opcoesTipoLembrete}
                  name="tipoLembrete"
                  control={control}
                  usarValidacaoParaFormulario={true}
                  register={register}
                  setValue={setValue}
                />
              </Col>
            </Row>
            <Row gutter={[16, 16]} className="mt-5">
              <Col xs={5} sm={5} md={5}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <CustomLabel>Permitir rubrica em todas as páginas? </CustomLabel>
                  <Controller
                    name="permitirRubrica"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FormControlLabel
                          value={true}
                          control={
                            <Radio
                              checked={field.value === true}
                              onChange={() => field.onChange(true)}
                              sx={{
                                color: grey[800],
                                "&.Mui-checked": { color: green[400] },
                              }}
                            />
                          }
                          label="Ativado"
                          sx={{ m: 0, "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                        />
                        <FormControlLabel
                          value={false}
                          control={
                            <Radio
                              checked={field.value === false}
                              onChange={() => field.onChange(false)}
                              sx={{
                                color: grey[800],
                                "&.Mui-checked": { color: green[400] },
                              }}
                            />
                          }
                          label="Desativado"
                          sx={{ m: 0, "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                        />
                      </div>
                    )}
                  />
                </div>
              </Col>
              <Col xs={10} sm={10} md={10}>
                <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '3dvw' }}>
                  <CustomLabel>Ordem de envio para os assinantes</CustomLabel>
                  <Controller
                    name="enviarEmOrdem"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <FormControlLabel
                          value={true}
                          control={
                            <Radio
                              checked={field.value === true}
                              onChange={() => field.onChange(true)}
                              sx={{
                                color: grey[800],
                                "&.Mui-checked": { color: green[400] },
                              }}
                            />
                          }
                          label="Enviar em ordem de assinatura"
                          sx={{ m: 0, "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                        />
                        <FormControlLabel
                          value={false}
                          control={
                            <Radio
                              checked={field.value === false}
                              onChange={() => field.onChange(false)}
                              sx={{
                                color: grey[800],
                                "&.Mui-checked": { color: green[400] },
                              }}
                            />
                          }
                          label="Enviar para todos os assinantes ao mesmo tempo"
                          sx={{ m: 0, "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                        />
                      </div>
                    )}
                  />
                </div>
              </Col>
            </Row>
            <Row gutter={[16, 16]} className="mt-4">
              <Col xs={24} sm={24} md={24}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <CustomLabel>Disponibilizar documento para condôminos</CustomLabel>
                  {/* Texto de apoio menor logo abaixo do label */}
                    <span style={{ fontSize: '0.75rem', color: '#666', marginBottom: '8px', lineHeight: '1.2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}
                    title="Defina se o documento ficará disponível para os condôminos no Group Com ao ser enviado para assinatura, mesmo com assinaturas pendentes.">
                      Defina se o documento ficará disponível para os condôminos no Group Com ao ser enviado para assinatura, mesmo com assinaturas pendentes.
                    </span>
                  <Controller
                    name="publicarParaCondominos"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FormControlLabel
                          value={true}
                          control={
                            <Radio
                              checked={field.value === true}
                              onChange={() => field.onChange(true)}
                              sx={{
                                color: grey[800],
                                "&.Mui-checked": { color: green[400] },
                              }}
                            />
                          }
                          label="Sim"
                          sx={{ m: 0, "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                        />
                        <FormControlLabel
                          value={false}
                          control={
                            <Radio
                              checked={field.value === false}
                              onChange={() => field.onChange(false)}
                              sx={{
                                color: grey[800],
                                "&.Mui-checked": { color: green[400] },
                              }}
                            />
                          }
                          label="Não"
                          sx={{ m: 0, "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                        />
                      </div>
                    )}
                  />
                </div>
              </Col>
            </Row>
          </div>
        </Section>
      </BodyPanel>
    </Panel>
  );
};